import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { OnboardingService } from '@/shared/api/services/onboardingService';
import { useAuth } from '@/shared/state/store';
import AddressVerification from '@/pages/private/onBoarding/AddressVerification';

export default function VerifyAddressGuard(): JSX.Element | null {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [allowed, setAllowed] = useState<boolean>(false);

  const userId = useMemo(() => {
    return (user as { userId?: number } | null | undefined)?.userId;
  }, [user]);

  useEffect(() => {
    const checkBeforeRender = async () => {
      try {
        if (!userId || typeof userId !== 'number') {
          setAllowed(false);
          return;
        }

        const details = await OnboardingService.getCustomerVerificationDetails(userId);

        // Extract verifyCustomer flag (Y/N) and handle immediate redirect if not required
        const getStringProp = (obj: unknown, key: string): string | undefined => {
          if (!obj || typeof obj !== 'object') return undefined;
          const v = (obj as Record<string, unknown>)[key];
          return typeof v === 'string' ? v : undefined;
        };

        const verifyCustomer = (getStringProp(details, 'verifyCustomer') || '').trim().toUpperCase();
        const customerVerified = (getStringProp(details, 'customerVerified') || '').trim().toUpperCase();

        const extractNoOfAttempts = (payload: unknown): number | null => {
          const coerceNumber = (val: unknown): number | null => {
            if (typeof val === 'number' && Number.isFinite(val)) return val;
            if (typeof val === 'string' && val.trim() !== '' && !Number.isNaN(Number(val))) return Number(val);
            return null;
          };
          const tryFromObject = (obj: Record<string, unknown> | null | undefined): number | null => {
            if (!obj) return null;
            const direct = coerceNumber((obj as { noOfAttempts?: unknown }).noOfAttempts);
            if (direct !== null) return direct;
            const data = (obj as { data?: unknown }).data as unknown;
            if (Array.isArray(data) && data.length > 0) {
              const last = data[data.length - 1] as Record<string, unknown>;
              return coerceNumber((last as { noOfAttempts?: unknown }).noOfAttempts);
            }
            if (data && typeof data === 'object') {
              return coerceNumber((data as { noOfAttempts?: unknown }).noOfAttempts);
            }
            return null;
          };
          if (payload && typeof payload === 'object') {
            return tryFromObject(payload as Record<string, unknown>);
          }
          return null;
        };

        const attempts = extractNoOfAttempts(details);
        if (attempts !== null && attempts >= 3) {
          const msg = 'Your account has been locked due to multiple incorrect postcode attempts. Email customer.support@iehub.co.uk to unlock.';
          try { await logout(); } catch {}
          // Small delay to ensure auth state updates before GuestOnly guard
          await new Promise((r) => setTimeout(r, 100));
          navigate('/auth', {
            replace: true,
            state: {
              magicLinkError: {
                message: 'Account Locked',
                description: msg
              }
            }
          });
          return;
        }

        // If API indicates verification is NOT required, redirect to onboarding immediately
        if (verifyCustomer === 'N' || customerVerified === 'Y') {
          try {
            localStorage.removeItem('pendingLcode');
            localStorage.removeItem('pendingLcode_temp');
          } catch {}
          // Clear browser query params before navigating to avoid carrying over old params
          if (window.location.search) {
            window.history.replaceState(null, '', window.location.pathname + window.location.hash);
          }
          navigate('/new-onboarding?step=0', { replace: true });
          return;
        }

        setAllowed(true);
      } catch (err) {
        // If check fails, allow page to render to avoid blocking user
        console.warn('VerifyAddressGuard: pre-check failed', err);
        setAllowed(true);
      }
    };
    void checkBeforeRender();
  }, [userId, logout, navigate]);

  if (!allowed) return null;
  return <AddressVerification />;
}


